import React from 'react';
import { Mail, Phone, Instagram, Twitter, Linkedin, ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { contactInfo } from '../utils/mock';
import AMFICompliance from './AMFICompliance';

const Footer = () => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-900 text-white relative">
      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="absolute -top-6 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-amber-600 hover:bg-amber-700 rounded-full flex items-center justify-center shadow-xl transition-all hover:scale-110"
      >
        <ArrowUp size={24} />
      </button>

      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="mb-6 bg-white p-6 rounded-lg inline-block">
              <img 
                src="https://customer-assets.emergentagent.com/job_rubicon-funds/artifacts/0u6chg4n_RF%20transparent%20logo%E2%84%A2.png" 
                alt="Rubicon Finservices" 
                className="h-32 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 leading-relaxed mb-6">
              Your trusted partner in financial success. We help individuals and families 
              build wealth through expert guidance in mutual funds, SIP investments, and 
              comprehensive financial planning.
            </p>
            <div className="flex gap-4">
              <a
                href={`https://instagram.com/${contactInfo.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-lg flex items-center justify-center transition-all hover:scale-110 p-1.5"
                style={{ background: 'linear-gradient(45deg, #f09433 0%, #e6683c 25%, #dc2743 50%, #cc2366 75%, #bc1888 100%)' }}
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png" 
                  alt="Instagram"
                  className="w-full h-full object-contain"
                />
              </a>
              <a
                href={`https://twitter.com/${contactInfo.twitter.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white rounded-lg flex items-center justify-center transition-all hover:scale-110 p-1.5"
              >
                <img 
                  src="https://about.x.com/content/dam/about-twitter/x/brand-toolkit/logo-black.png.twimg.1920.png" 
                  alt="X"
                  className="w-full h-full object-contain"
                />
              </a>
              <a
                href={`https://${contactInfo.linkedin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-[#0A66C2] rounded-lg flex items-center justify-center transition-all hover:scale-110 p-1.5"
              >
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png" 
                  alt="LinkedIn"
                  className="w-full h-full object-contain"
                />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <button onClick={() => handleNavigation('/')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/about')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/services')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/calculators')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  Calculators
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/blog')} className="text-slate-400 hover:text-amber-400 transition-colors" data-testid="footer-blog-link">
                  Blog
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/contact')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  Contact
                </button>
              </li>
              <li>
                <button onClick={() => handleNavigation('/admin/login')} className="text-slate-400 hover:text-amber-400 transition-colors">
                  Team Login
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6">Contact Us</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Mail className="text-amber-400 flex-shrink-0 mt-1" size={18} />
                <a href={`mailto:${contactInfo.email}`} className="text-slate-400 hover:text-amber-400 transition-colors">
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="text-amber-400 flex-shrink-0 mt-1" size={18} />
                <a href={`tel:${contactInfo.phone}`} className="text-slate-400 hover:text-amber-400 transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* AMFI Compliance Section */}
        <div className="mb-8">
          <AMFICompliance />
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} Rubicon Finservices. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm text-slate-500">
              <button onClick={() => handleNavigation('/privacy')} className="hover:text-amber-400 transition-colors" data-testid="footer-privacy-link">Privacy Policy</button>
              <button onClick={() => handleNavigation('/data-request')} className="hover:text-amber-400 transition-colors" data-testid="footer-data-request-link">Data Rights</button>
              <button className="hover:text-amber-400 transition-colors">Disclaimer</button>
            </div>
          </div>
          
          {/* Registration Numbers */}
          <div className="mt-6 text-center">
            <p className="text-slate-400 text-sm mb-2 font-semibold">Registration Details</p>
            <div className="flex flex-wrap justify-center gap-4 text-slate-500 text-xs">
              <span className="bg-slate-800 px-4 py-2 rounded-lg">ARN: 102867</span>
              <span className="bg-slate-800 px-4 py-2 rounded-lg">APMI: APMI-APRN01145</span>
            </div>
          </div>
          
          <p className="text-slate-600 text-xs mt-6 text-center leading-relaxed">
            <strong className="text-red-400">⚠️ RISK DISCLAIMER:</strong> Mutual fund investments are subject to market risks. Please read all scheme related documents carefully before investing. 
            Past performance is not indicative of future returns. The NAV of schemes may fluctuate based on market conditions. 
            Consult your financial advisor before investing.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
